import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { UTILITY_TYPE_ID_LIABILITY_INSURANCE } from 'common/utility/constants';

import {
  getUtilityConfigurationSourceDisplayName,
  isUtilityConfigurationPersonSource,
  isUtilityConfigurationPmsSource,
  UTILITY_CONFIGURATION_SOURCE_ADMIN,
  UTILITY_CONFIGURATION_SOURCE_PARTNER,
  utilityConfigurationProvenancePropType,
} from 'client/constants/utilityConfigurationSource';
import { VISUALLY_HIDDEN_STYLE } from 'client/constants/visuallyHiddenStyle';
import SrBox from 'client/components/SrBox';
import SrButton, {
  SR_BUTTON_FONT_SIZE_SMALL,
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrDarkModal from 'client/components/SrDarkModal';
import SrFlex from 'client/components/SrFlex';
import SrModalContent from 'client/components/SrModalContent';
import SrText from 'client/components/SrText';
import {
  canDismissBuildingUnitsConfirmation,
  getSelectedBuildingUnitTargets,
} from 'client/components/SrUtilityProfitAddressesDataTable/buildingUnits.helpers';

import './SrUtilityBuildingUnitsConfirmationModal.less';

const getUtilityLabel = ({ id, label }) =>
  id === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? 'Insurance' : label;

const siblingUnitPropType = PropTypes.shape({
  addressDisplay: PropTypes.string.isRequired,
  addressId: PropTypes.number.isRequired,
  currentUtilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  utilityConfigurationProvenance: utilityConfigurationProvenancePropType,
});

const sourceUnitPropType = PropTypes.shape({
  addressDisplay: PropTypes.string.isRequired,
  addressId: PropTypes.number.isRequired,
  previousUtilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  reviewSource: PropTypes.oneOf([
    UTILITY_CONFIGURATION_SOURCE_ADMIN,
    UTILITY_CONFIGURATION_SOURCE_PARTNER,
  ]),
  utilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
});

const FOCUSABLE_ELEMENT_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const getUtilityChanges = ({ currentUtilityTypeIds, utilityTypeIds, utilityTypes }) => {
  const currentUtilityTypeIdSet = new Set(currentUtilityTypeIds);
  const nextUtilityTypeIdSet = new Set(utilityTypeIds);

  return utilityTypes.reduce((changes, utilityType) => {
    const wasEnabled = currentUtilityTypeIdSet.has(utilityType.id);
    const willBeEnabled = nextUtilityTypeIdSet.has(utilityType.id);
    if (wasEnabled !== willBeEnabled) {
      changes.push({
        label: getUtilityLabel(utilityType),
        status: willBeEnabled ? 'Enabled' : 'Disabled',
      });
    }
    return changes;
  }, []);
};

const getReviewerLabel = (reviewSource) =>
  reviewSource === UTILITY_CONFIGURATION_SOURCE_ADMIN
    ? getUtilityConfigurationSourceDisplayName(reviewSource)
    : 'your team';

export const getUnitReviewStatus = ({
  isSourceUnit,
  isSelected,
  isTarget,
  reviewSource,
  utilityConfigurationProvenance,
}) => {
  const reviewerLabel = getReviewerLabel(reviewSource);

  if (isSourceUnit) {
    return `Saved and reviewed by ${reviewerLabel}`;
  }
  if (isTarget && isSelected === false) {
    return 'Not selected';
  }
  if (isTarget) {
    return `Will be reviewed by ${reviewerLabel}`;
  }

  const configurationSource = utilityConfigurationProvenance?.source;
  if (isUtilityConfigurationPersonSource(configurationSource)) {
    return `Already reviewed by ${getReviewerLabel(configurationSource)}`;
  }
  if (isUtilityConfigurationPmsSource(configurationSource)) {
    return `Already synced from ${getUtilityConfigurationSourceDisplayName(
      configurationSource,
    )}`;
  }

  return `Will be reviewed by ${reviewerLabel}`;
};

export const UtilityChanges = ({
  currentUtilityTypeIds,
  matchingUtilityChanges = [],
  noChangeLabel = 'No utility changes',
  reviewStatus,
  utilityTypeIds,
  utilityTypes,
}) => {
  const changes = getUtilityChanges({ currentUtilityTypeIds, utilityTypeIds, utilityTypes });
  const alreadyMatchingLabels = matchingUtilityChanges.map(
    ({ label, status }) => `${label} already ${status.toLowerCase()}`,
  );

  return (
    <SrFlex direction="col" rowGap="2px">
      {changes.length === 0 ? (
        (alreadyMatchingLabels.length > 0 ? alreadyMatchingLabels : [noChangeLabel]).map(
          (label) => (
            <SrText
              key={label}
              display="block"
              font="greycliff"
              fontSize="13px"
              lineHeight="18px"
              weight="600"
              color="blueGreycliff"
            >
              {label}
            </SrText>
          ),
        )
      ) : (
        changes.map(({ label, status }) => (
          <SrText
            key={`${label}-${status}`}
            display="block"
            font="greycliff"
            fontSize="13px"
            lineHeight="18px"
            weight="600"
            color={status === 'Enabled' ? 'green100' : 'red'}
          >
            {`${label} → ${status}`}
          </SrText>
        ))
      )}
      {reviewStatus && (
        <SrText
          display="block"
          font="greycliff"
          fontSize="12px"
          lineHeight="17px"
          weight="500"
          color="grey500"
        >
          {reviewStatus}
        </SrText>
      )}
    </SrFlex>
  );
};

UtilityChanges.propTypes = {
  currentUtilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  matchingUtilityChanges: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['Enabled', 'Disabled']).isRequired,
    }),
  ),
  noChangeLabel: PropTypes.string,
  reviewStatus: PropTypes.string,
  utilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  utilityTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// The scrollable results region must remain keyboard-focusable.
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
export const SiblingUnitsList = ({
  buildingDisplay,
  excludedSiblingAddressIds,
  isApplying,
  onToggleSiblingUnit,
  siblingUnits,
  sourceUnit,
  stackRows,
  targetSiblingUnits,
  utilityTypeIds,
  utilityTypes,
}) => {
  const unitLabel = siblingUnits.length === 1 ? 'unit' : 'units';
  const excludedSiblingAddressIdSet = new Set(excludedSiblingAddressIds);
  const targetSiblingAddressIds = new Set(targetSiblingUnits.map(({ addressId }) => addressId));
  const units = [
    {
      addressDisplay: sourceUnit.addressDisplay,
      addressId: sourceUnit.addressId,
      currentUtilityTypeIds: sourceUnit.previousUtilityTypeIds,
      isSourceUnit: true,
    },
    ...siblingUnits,
  ];
  const sourceUtilityChanges = getUtilityChanges({
    currentUtilityTypeIds: sourceUnit.previousUtilityTypeIds,
    utilityTypeIds: sourceUnit.utilityTypeIds,
    utilityTypes,
  });
  const gridTemplateColumns = stackRows
    ? '28px minmax(0, 1fr)'
    : '32px minmax(0, 1.45fr) minmax(150px, 0.9fr)';

  return (
    <SrBox
      className="buildingUnitsListSection"
      display="flex"
      flex="1 1 auto"
      flexDirection="column"
      marginTop="20px"
      minHeight="0"
      overflow="hidden"
    >
      <SrText
        display="block"
        font="greycliff"
        fontSize="14px"
        lineHeight="20px"
        weight="800"
        color="blueGreycliff"
        marginBottom="8px"
      >
        Units at this address ({units.length})
      </SrText>
      <div
        className="buildingUnitsListScroll"
        role="region"
        aria-label={`Saved unit and ${siblingUnits.length} other ${unitLabel} at ${buildingDisplay}`}
        tabIndex={0}
        data-testid="building-units-list"
        style={{
          border: '1px solid #e9ebec',
          borderRadius: '12px',
        }}
      >
        {!stackRows && (
          <SrBox
            className="buildingUnitsListHeader"
            bgColor="grey100"
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            padding="10px 14px"
            position="sticky"
            top="0"
            zIndex="1"
          >
            <SrText font="greycliff" fontSize="12px" lineHeight="16px" weight="800">
              Apply
            </SrText>
            <SrText font="greycliff" fontSize="12px" lineHeight="16px" weight="800">
              Address
            </SrText>
            <SrText font="greycliff" fontSize="12px" lineHeight="16px" weight="800">
              Changes
            </SrText>
          </SrBox>
        )}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {units.map(
            (
              {
                addressDisplay,
                addressId,
                currentUtilityTypeIds,
                isSourceUnit = false,
                utilityConfigurationProvenance,
              },
              index,
            ) => {
              const [streetDisplay, ...locationParts] = addressDisplay.split(', ');
              const locationDisplay = locationParts.join(', ');
              const isTarget = !isSourceUnit && targetSiblingAddressIds.has(addressId);
              const isSelected = isTarget && !excludedSiblingAddressIdSet.has(addressId);
              const reviewStatus = getUnitReviewStatus({
                isSourceUnit,
                isSelected,
                isTarget,
                reviewSource: sourceUnit.reviewSource,
                utilityConfigurationProvenance,
              });
              const nextUtilityTypeIds = isSourceUnit
                ? sourceUnit.utilityTypeIds
                : utilityTypeIds;
              const sourceRowClassName = isSourceUnit
                ? `buildingUnitsSavedUnitRow${
                    stackRows ? ' buildingUnitsSavedUnitRowStacked' : ''
                  }`
                : undefined;
              const rowClassName = [
                sourceRowClassName,
                isTarget && !isSelected ? 'buildingUnitsExcludedRow' : null,
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <li
                  key={addressId}
                  className={rowClassName || undefined}
                  data-testid={`building-unit-row-${addressId}`}
                  style={{ borderTop: index === 0 && stackRows ? 0 : '1px solid #e9ebec' }}
                >
                  <SrBox
                    display="grid"
                    gridTemplateColumns={gridTemplateColumns}
                    padding="12px 14px"
                    rowGap={stackRows ? '8px' : undefined}
                  >
                    <div style={{ alignSelf: 'start', gridColumn: 1, gridRow: 1 }}>
                      {isTarget && (
                        <input
                          aria-label={`Apply settings to ${addressDisplay}`}
                          checked={isSelected}
                          className="buildingUnitsSelectionCheckbox"
                          disabled={isApplying}
                          type="checkbox"
                          onChange={() => onToggleSiblingUnit(addressId)}
                        />
                      )}
                    </div>
                    <div style={{ gridColumn: 2, gridRow: 1 }}>
                      <SrFlex align="center" columnGap="8px" wrap="wrap">
                        <SrText
                          display="block"
                          font="greycliff"
                          fontSize="14px"
                          lineHeight="19px"
                          weight="700"
                          color="blueGreycliff"
                        >
                          {streetDisplay}
                        </SrText>
                        {isSourceUnit && (
                          <span className="buildingUnitsSavedUnitBadge">Saved unit</span>
                        )}
                      </SrFlex>
                      {locationDisplay && (
                        <SrText
                          display="block"
                          font="greycliff"
                          fontSize="12px"
                          lineHeight="17px"
                          weight="500"
                          color="grey500"
                          marginTop="2px"
                        >
                          {locationDisplay}
                        </SrText>
                      )}
                    </div>
                    <div
                      className={isTarget && !isSelected ? 'buildingUnitsExcludedChanges' : undefined}
                      role="group"
                      aria-label={`Changes for ${addressDisplay}`}
                      style={{ gridColumn: stackRows ? 2 : 3, gridRow: stackRows ? 2 : 1 }}
                    >
                      <UtilityChanges
                        currentUtilityTypeIds={currentUtilityTypeIds}
                        matchingUtilityChanges={isSourceUnit ? [] : sourceUtilityChanges}
                        noChangeLabel={isSourceUnit ? 'No utility changes' : 'Utilities already match'}
                        reviewStatus={reviewStatus}
                        utilityTypeIds={nextUtilityTypeIds}
                        utilityTypes={utilityTypes}
                      />
                    </div>
                  </SrBox>
                </li>
              );
            },
          )}
        </ul>
      </div>
    </SrBox>
  );
};
/* eslint-enable jsx-a11y/no-noninteractive-tabindex */

SiblingUnitsList.propTypes = {
  buildingDisplay: PropTypes.string.isRequired,
  excludedSiblingAddressIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isApplying: PropTypes.bool.isRequired,
  onToggleSiblingUnit: PropTypes.func.isRequired,
  siblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  sourceUnit: sourceUnitPropType.isRequired,
  stackRows: PropTypes.bool,
  targetSiblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  utilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  utilityTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const SrUtilityBuildingUnitsConfirmationContent = ({
  buildingDisplay,
  dialogRef,
  errorMessage,
  excludedSiblingAddressIds,
  isApplying,
  onApply,
  onClose,
  onToggleSiblingUnit,
  onlyThisUnitButtonContainerRef,
  previewIsLoading,
  previewFailed,
  siblingUnits,
  sourceUnit,
  stackActions,
  targetSiblingUnits,
  utilityTypeIds,
  utilityTypes,
}) => {
  const siblingUnitCount = siblingUnits.length;
  const unitLabel = siblingUnitCount === 1 ? 'unit' : 'units';
  const targetSiblingUnitCount = targetSiblingUnits.length;
  const selectedSiblingUnitCount = getSelectedBuildingUnitTargets({
    excludedSiblingAddressIds,
    targetSiblingUnits,
  }).length;
  const selectedUnitLabel = selectedSiblingUnitCount === 1 ? 'unit' : 'units';
  const hasTargets = targetSiblingUnitCount > 0;
  let title = hasTargets
    ? 'Apply these settings to other units?'
    : 'Other units already have these settings';
  if (previewIsLoading) {
    title = 'Checking other units…';
  } else if (previewFailed) {
    title = 'Couldn’t check other units';
  }
  let primaryActionLabel = `Apply to ${selectedSiblingUnitCount} ${selectedUnitLabel}`;
  if (previewFailed) {
    primaryActionLabel = isApplying ? 'Checking…' : 'Try again';
  } else if (isApplying) {
    primaryActionLabel = 'Applying…';
  }
  const keepSavedUnitAction = (
    <SrBox
      ref={onlyThisUnitButtonContainerRef}
      flex={stackActions ? undefined : '1'}
      width="100%"
    >
      <SrButton
        disabled={isApplying && !previewIsLoading}
        fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
        heightType={SR_BUTTON_HEIGHT_SHORT}
        padding="0 16px 2px"
        theme={SR_BUTTON_THEME_ADOBE_GREY}
        width="100%"
        onClick={onClose}
      >
        Keep only saved unit
      </SrButton>
    </SrBox>
  );
  const primaryAction = (
    <SrBox flex={stackActions ? undefined : '1'} width="100%">
      <SrButton
        disabled={isApplying || (!previewFailed && selectedSiblingUnitCount === 0)}
        fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
        heightType={SR_BUTTON_HEIGHT_SHORT}
        padding="0 16px 2px"
        theme={SR_BUTTON_THEME_ADOBE_BLUE}
        width="100%"
        onClick={onApply}
      >
        {primaryActionLabel}
      </SrButton>
    </SrBox>
  );
  const doneAction = (
    <SrBox ref={onlyThisUnitButtonContainerRef} width="100%">
      <SrButton
        fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
        heightType={SR_BUTTON_HEIGHT_SHORT}
        padding="0 16px 2px"
        theme={SR_BUTTON_THEME_ADOBE_BLUE}
        width="100%"
        onClick={onClose}
      >
        Done
      </SrButton>
    </SrBox>
  );
  let description = `We found ${siblingUnitCount} other ${unitLabel} at ${buildingDisplay}. `;
  if (!hasTargets) {
    description +=
      'Every other unit already has these settings from a reviewed or synced source, ' +
      'so no changes need to be applied. ';
  }
  description += 'The unit you just saved is shown first.';
  if (hasTargets) {
    description += ' Uncheck any units you do not want to update.';
  }
  let descriptionRole = 'status';
  if (previewIsLoading) {
    description = 'This unit was saved. We’re checking for other units in the building.';
  } else if (previewFailed) {
    description =
      'This unit was saved, but we couldn’t check for other units in the building. ' +
      'Try again, or keep the change on only this unit.';
    descriptionRole = 'alert';
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      aria-labelledby="building-units-modal-title"
      aria-describedby="building-units-modal-description"
      className="buildingUnitsConfirmationDialog"
      data-testid="building-units-confirmation-modal"
    >
      <div className="buildingUnitsConfirmationHeader">
        <SrText
          id="building-units-modal-title"
          display="block"
          font="greycliff"
          fontSize="24px"
          lineHeight="30px"
          weight="800"
          color="blueGreycliff"
        >
          {title}
        </SrText>
        <SrText
          id="building-units-modal-description"
          role={descriptionRole}
          aria-live={descriptionRole === 'status' ? 'polite' : undefined}
          display="block"
          font="greycliff"
          fontSize="16px"
          lineHeight="23px"
          weight="500"
          color="blueGreycliff"
          marginTop="10px"
        >
          {description}
        </SrText>
      </div>

      {!previewIsLoading && !previewFailed && (
        <SiblingUnitsList
          buildingDisplay={buildingDisplay}
          excludedSiblingAddressIds={excludedSiblingAddressIds}
          isApplying={isApplying}
          onToggleSiblingUnit={onToggleSiblingUnit}
          siblingUnits={siblingUnits}
          sourceUnit={sourceUnit}
          stackRows={stackActions}
          targetSiblingUnits={targetSiblingUnits}
          utilityTypeIds={utilityTypeIds}
          utilityTypes={utilityTypes}
        />
      )}

      {errorMessage && (
        <SrText
          role="alert"
          display="block"
          font="greycliff"
          fontSize="14px"
          lineHeight="20px"
          weight="600"
          color="red"
          className="buildingUnitsConfirmationError"
          marginTop="16px"
        >
          {errorMessage}
        </SrText>
      )}

      {isApplying && !previewIsLoading && (
        <span role="status" aria-live="polite" style={VISUALLY_HIDDEN_STYLE}>
          {previewFailed ? 'Checking other units…' : 'Applying settings to the other units…'}
        </span>
      )}

      <SrFlex
        className="buildingUnitsConfirmationActions"
        columnGap={stackActions ? undefined : '12px'}
        direction={stackActions ? 'col' : 'row'}
        marginTop="28px"
        rowGap={stackActions ? '12px' : undefined}
      >
        {previewIsLoading ? (
          keepSavedUnitAction
        ) : previewFailed ? (
          stackActions ? (
            <>
              {primaryAction}
              {keepSavedUnitAction}
            </>
          ) : (
            <>
              {keepSavedUnitAction}
              {primaryAction}
            </>
          )
        ) : !hasTargets ? (
          doneAction
        ) : stackActions ? (
          <>
            {primaryAction}
            {keepSavedUnitAction}
          </>
        ) : (
          <>
            {keepSavedUnitAction}
            {primaryAction}
          </>
        )}
      </SrFlex>
    </div>
  );
};

SrUtilityBuildingUnitsConfirmationContent.propTypes = {
  buildingDisplay: PropTypes.string,
  dialogRef: PropTypes.object,
  errorMessage: PropTypes.string,
  excludedSiblingAddressIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isApplying: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleSiblingUnit: PropTypes.func.isRequired,
  onlyThisUnitButtonContainerRef: PropTypes.object.isRequired,
  previewIsLoading: PropTypes.bool,
  previewFailed: PropTypes.bool,
  siblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  sourceUnit: sourceUnitPropType.isRequired,
  stackActions: PropTypes.bool,
  targetSiblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  utilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  utilityTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const SrUtilityBuildingUnitsConfirmationModal = (props) => {
  const { isApplying, onClose, previewIsLoading, returnFocusElement } = props;
  const dialogRef = useRef(null);
  const onlyThisUnitButtonContainerRef = useRef(null);
  const stackActions = useMediaQuery({ query: '(max-width: 420px)' });
  const compactHeight = useMediaQuery({ query: '(max-height: 600px)' });
  const canDismiss = canDismissBuildingUnitsConfirmation({ isApplying, previewIsLoading });

  useEffect(() => {
    const appRoot = global.document.getElementById('react-container');
    const previousAriaHidden = appRoot?.getAttribute('aria-hidden');
    const appRootWasInert = appRoot?.hasAttribute('inert');

    if (appRoot) {
      appRoot.setAttribute('aria-hidden', 'true');
      appRoot.setAttribute('inert', '');
    }

    return () => {
      if (appRoot) {
        if (previousAriaHidden === null) {
          appRoot.removeAttribute('aria-hidden');
        } else {
          appRoot.setAttribute('aria-hidden', previousAriaHidden);
        }
        if (!appRootWasInert) {
          appRoot.removeAttribute('inert');
        }
      }
      if (returnFocusElement?.isConnected && typeof returnFocusElement.focus === 'function') {
        returnFocusElement.focus();
      }
    };
  }, [returnFocusElement]);

  useEffect(() => {
    const button = onlyThisUnitButtonContainerRef.current?.querySelector('button');
    let focusTimer;

    if (button) {
      focusTimer = setTimeout(() => button.focus(), 0);
    } else if (dialogRef.current) {
      dialogRef.current.focus();
    }

    return () => clearTimeout(focusTimer);
  }, [previewIsLoading]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && canDismiss) {
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const focusableElements = Array.from(
          dialogRef.current?.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR) ?? [],
        );
        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];
        const activeElement = global.document.activeElement;
        const focusIsOutsideDialog = !dialogRef.current?.contains(activeElement);

        if (event.shiftKey && (activeElement === firstFocusableElement || focusIsOutsideDialog)) {
          event.preventDefault();
          lastFocusableElement.focus();
        } else if (
          !event.shiftKey &&
          (activeElement === lastFocusableElement || focusIsOutsideDialog)
        ) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canDismiss, onClose]);

  return (
    <SrDarkModal
      className="SrUtilityBuildingUnitsConfirmationModal"
      onClose={canDismiss ? onClose : undefined}
    >
      <SrModalContent
        width={560}
        innerStyle={{
          minWidth: 0,
          padding: compactHeight ? '20px' : stackActions ? '24px 20px' : '32px',
        }}
      >
        <SrUtilityBuildingUnitsConfirmationContent
          {...props}
          dialogRef={dialogRef}
          onlyThisUnitButtonContainerRef={onlyThisUnitButtonContainerRef}
          stackActions={stackActions}
        />
      </SrModalContent>
    </SrDarkModal>
  );
};

SrUtilityBuildingUnitsConfirmationModal.propTypes = {
  buildingDisplay: PropTypes.string,
  errorMessage: PropTypes.string,
  excludedSiblingAddressIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isApplying: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleSiblingUnit: PropTypes.func.isRequired,
  previewFailed: PropTypes.bool,
  previewIsLoading: PropTypes.bool,
  returnFocusElement: PropTypes.object,
  siblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  sourceUnit: sourceUnitPropType.isRequired,
  targetSiblingUnits: PropTypes.arrayOf(siblingUnitPropType).isRequired,
  utilityTypeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  utilityTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

SrUtilityBuildingUnitsConfirmationModal.displayName =
  'SrUtilityBuildingUnitsConfirmationModal';

export default SrUtilityBuildingUnitsConfirmationModal;
